import { useEffect, useState } from 'react'
import { openMessages, type MessagePush } from '../api/messages'

/**
 * Messages that arrived while this tab was open.
 *
 * 🔴 IT IS A HEAD, NOT THE RECORD, and every screen that uses it must merge
 * rather than replace. `useParentEvents` and `useStaffEvents` say the same thing
 * for the same reason: a reload clears this list, and what is durable is the
 * thread the screen fetches. Persisting would imply an inbox nothing on the
 * server backs.
 *
 * 🔴 ONE SOCKET PER TIER, NOT ONE PER THREAD. A teacher with thirty children
 * opens one connection and filters by `playerId`; a socket per open conversation
 * would be thirty handshakes to read one class's messages, and a teacher who
 * clicked through her roster would leave a trail of them.
 */
export function useMessages(token: string | undefined, limit = 50): MessagePush[] {
  const [messages, setMessages] = useState<MessagePush[]>([])

  useEffect(() => {
    /*
     * ⚠️ NO TOKEN, NO CONNECTION. A socket opened without one is disconnected by
     * the server on the handshake, so attempting it would be a reconnect loop
     * against a door that is never going to open.
     */
    if (token === undefined || token === '') return undefined

    return openMessages(token, (event) => {
      setMessages((prev) => {
        /*
         * ⚠️ DEDUPED ON THE ROW'S OWN ID. socket.io redelivers on reconnect in
         * some transports, and the same message appearing twice in a
         * conversation is the kind of bug a parent screenshots.
         */
        if (prev.some((m) => m.id === event.id)) return prev
        return [...prev, event].slice(-limit)
      })
    })
  }, [token, limit])

  return messages
}

/**
 * The fetched thread with anything that arrived since folded in.
 *
 * 🔴 THE FETCHED ROW WINS ON A COLLISION. A frame is a copy of what the server
 * wrote; the fetched row IS what the server wrote, and it carries `readAt`,
 * which a frame cannot know. So a live message already in the thread is nothing
 * new to show.
 *
 * ⚠️ `mine` IS FALSE FOR EVERY MERGED FRAME, AND THAT IS CORRECT RATHER THAN AN
 * APPROXIMATION: the server pushes only to the OTHER side. A message you sent
 * arrives back as the write's own response, never down the socket.
 */
export function mergeThread<
  T extends {
    id: string
    senderRole: string
    senderLabel: string
    body: string
    createdAt: string
  },
>(fetched: readonly T[], live: readonly MessagePush[], playerId: string): T[] {
  const known = new Set(fetched.map((m) => m.id))
  const extra = live
    .filter((m) => m.playerId === playerId && !known.has(m.id))
    .map(
      (m) =>
        ({
          id: m.id,
          senderRole: m.senderRole,
          senderLabel: m.senderLabel,
          body: m.body,
          createdAt: m.at,
          readAt: null,
          mine: false,
        }) as unknown as T,
    )

  /* Oldest first — a conversation is read downwards, and the fetch already is. */
  return [...fetched, ...extra].sort((a, z) => a.createdAt.localeCompare(z.createdAt))
}
